import { CHARSET_NUMERIC, GLYPH_HEIGHT, buildLineAnchors, composeGlyphSequenceBinaryColumns, decodeLine, getDefaultGlyphAtlas, } from 'maplestory-glyph-decoder';
const PAGE_INDICATOR_CHARSET = CHARSET_NUMERIC + '[]/';
const PAGE_INDICATOR_PATTERN = /^\[[0-9]+\/[0-9]+\]$/u;
const PAGE_DIGIT_WIDTH = 7;
function atlasChoices(options) {
    if (options.atlas) {
        return [options.atlas];
    }
    if (options.atlasVariant === 'cleartype-rgb' || options.atlasVariant === 'cleartype-bgr') {
        return [getDefaultGlyphAtlas({
                variant: options.atlasVariant,
                charset: PAGE_INDICATOR_CHARSET,
            })];
    }
    return ['cleartype-rgb', 'cleartype-bgr'].map((variant) => (getDefaultGlyphAtlas({ variant, charset: PAGE_INDICATOR_CHARSET })));
}
function requirePageGlyph(atlas, char) {
    const glyph = atlas.byChar.get(char);
    if (!glyph)
        throw new Error(`page indicator atlas is missing ${JSON.stringify(char)}`);
    return glyph;
}
function validatePageAtlas(atlas) {
    for (const char of PAGE_INDICATOR_CHARSET)
        requirePageGlyph(atlas, char);
    for (const digit of CHARSET_NUMERIC) {
        const glyph = requirePageGlyph(atlas, digit);
        if (glyph.width !== PAGE_DIGIT_WIDTH) {
            throw new Error(`page digit ${digit} must have logical width ${PAGE_DIGIT_WIDTH}, got ${glyph.width}`);
        }
    }
}
/** Exact delimiter locator.
 *
 * Anchor equality rejects any missing or additional fully-black core pixel.
 * Binary equality additionally rejects weak non-white pixels not present in the
 * atlas. Only the glyph's logical width is compared here: a right overhang may
 * share columns with the next glyph and is confirmed later as part of the full
 * composed page string.
 */
function logicalGlyphMatches(line, x, glyph) {
    if (x < 0 || x + glyph.width > line.width)
        return false;
    if (line.anchorString.substring(x, x + glyph.width) !== glyph.anchorKey)
        return false;
    for (let dx = 0; dx < glyph.width; dx++) {
        if ((line.nonWhiteColumns[x + dx] ?? 0) !== (glyph.binaryColumns[dx] ?? 0))
            return false;
    }
    return true;
}
function findLogicalGlyph(line, glyph) {
    const positions = [];
    for (let x = 0; x + glyph.width <= line.width; x++) {
        if (logicalGlyphMatches(line, x, glyph))
            positions.push(x);
    }
    return positions;
}
function locateDelimiters(line, atlas) {
    return {
        open: findLogicalGlyph(line, requirePageGlyph(atlas, '[')),
        slash: findLogicalGlyph(line, requirePageGlyph(atlas, '/')),
        close: findLogicalGlyph(line, requirePageGlyph(atlas, ']')),
    };
}
function glyphsForText(atlas, text) {
    const glyphs = [];
    for (const char of text) {
        const glyph = atlas.byChar.get(char);
        if (!glyph)
            return null;
        glyphs.push(glyph);
    }
    return glyphs;
}
function composedAnchorKey(glyphs) {
    return glyphs.map((glyph) => glyph.anchorKey).join('');
}
/** Confirm the complete page text, including every delimiter overhang and any
 * overlap with the following digit, against exact anchor and binary evidence.
 */
function composedTextMatches(line, startX, atlas, text) {
    const glyphs = glyphsForText(atlas, text);
    if (!glyphs)
        return false;
    const rendered = composeGlyphSequenceBinaryColumns(glyphs);
    if (startX < 0 || startX + rendered.visualWidth > line.width)
        return false;
    if (line.anchorString.substring(startX, startX + rendered.logicalWidth) !== composedAnchorKey(glyphs)) {
        return false;
    }
    for (let dx = 0; dx < rendered.visualWidth; dx++) {
        if ((line.nonWhiteColumns[startX + dx] ?? 0) !== (rendered.columns[dx] ?? 0)) {
            return false;
        }
    }
    return true;
}
function pageLine(image) {
    if (image.width < 1 || image.height !== GLYPH_HEIGHT)
        return null;
    const line = buildLineAnchors(image, {
        profile: 'black',
        inkEvidenceThreshold: 0.08,
        bboxPad: image.width,
        endPad: 0,
    });
    return line.inkX1 > line.inkX0 || line.exactX1 > line.exactX0 ? line : null;
}
function uniqueResult(results) {
    const unique = [...new Set(results.filter(Boolean))];
    return unique.length === 1 ? unique[0] : '';
}
function decodeLeftToRightWithAtlas(line, atlas, options) {
    validatePageAtlas(atlas);
    const openGlyph = requirePageGlyph(atlas, '[');
    const closeGlyph = requirePageGlyph(atlas, ']');
    const openPositions = findLogicalGlyph(line, openGlyph);
    const closePositions = findLogicalGlyph(line, closeGlyph);
    const results = [];
    for (const openX of openPositions) {
        for (const closeX of closePositions) {
            if (closeX <= openX + openGlyph.width)
                continue;
            const endX = closeX + closeGlyph.width;
            const decoded = decodeLine(atlas, line, {
                startX: openX,
                endX,
                beamSize: options.beamSize,
                branchLimit: options.branchLimit,
                confidenceFloor: options.confidenceFloor,
                useOverhangEvidence: true,
            });
            if (!PAGE_INDICATOR_PATTERN.test(decoded.text))
                continue;
            const glyphs = glyphsForText(atlas, decoded.text);
            if (!glyphs)
                continue;
            const rendered = composeGlyphSequenceBinaryColumns(glyphs);
            const decodedCloseX = openX + rendered.logicalWidth - closeGlyph.width;
            if (decodedCloseX !== closeX)
                continue;
            if (!composedTextMatches(line, openX, atlas, decoded.text))
                continue;
            results.push(decoded.text);
        }
    }
    return results;
}
/** Locate `[` and `]` by exact anchors and logical binary masks, then run the
 * normal left-to-right glyph decoder from the known opening-bracket position.
 * The complete `\[digits/digits\]` sequence is accepted only after exact
 * composed anchor and binary-mask confirmation.
 */
export function decodeOwlPageIndicatorLeftToRight(image, options = {}) {
    const line = pageLine(image);
    if (!line)
        return '';
    return uniqueResult(atlasChoices(options).flatMap((atlas) => decodeLeftToRightWithAtlas(line, atlas, options)));
}
function digitSlotMatches(line, slotX, previousGlyph, digitGlyph) {
    if (slotX < 0 || slotX + digitGlyph.width > line.width)
        return false;
    if (line.anchorString.substring(slotX, slotX + digitGlyph.width) !== digitGlyph.anchorKey)
        return false;
    // Compare the exact composed binary shape in this logical slot. This folds a
    // preceding delimiter's right overhang into the digit's first column before
    // comparing the shared logical slot.
    const rendered = composeGlyphSequenceBinaryColumns([previousGlyph, digitGlyph]);
    const slotOffset = previousGlyph.width;
    for (let dx = 0; dx < digitGlyph.width; dx++) {
        const expected = rendered.columns[slotOffset + dx] ?? 0;
        if ((line.nonWhiteColumns[slotX + dx] ?? 0) !== expected)
            return false;
    }
    return true;
}
function decodeDigitSlots(line, atlas, startX, count, previousGlyph) {
    let text = '';
    let previous = previousGlyph;
    for (let index = 0; index < count; index++) {
        const slotX = startX + index * PAGE_DIGIT_WIDTH;
        const candidates = [];
        for (const digit of CHARSET_NUMERIC) {
            const glyph = requirePageGlyph(atlas, digit);
            if (digitSlotMatches(line, slotX, previous, glyph))
                candidates.push(glyph);
        }
        if (candidates.length !== 1)
            return null;
        const selected = candidates[0];
        text += selected.char;
        previous = selected;
    }
    return text;
}
function decodeSlotsWithAtlas(line, atlas) {
    validatePageAtlas(atlas);
    const delimiters = locateDelimiters(line, atlas);
    const openGlyph = requirePageGlyph(atlas, '[');
    const slashGlyph = requirePageGlyph(atlas, '/');
    const closeGlyph = requirePageGlyph(atlas, ']');
    const results = [];
    for (const openX of delimiters.open) {
        const leftStartX = openX + openGlyph.width;
        for (const slashX of delimiters.slash) {
            const leftWidth = slashX - leftStartX;
            if (leftWidth < PAGE_DIGIT_WIDTH || leftWidth % PAGE_DIGIT_WIDTH !== 0)
                continue;
            const leftCount = leftWidth / PAGE_DIGIT_WIDTH;
            const left = decodeDigitSlots(line, atlas, leftStartX, leftCount, openGlyph);
            if (!left)
                continue;
            const rightStartX = slashX + slashGlyph.width;
            for (const closeX of delimiters.close) {
                const rightWidth = closeX - rightStartX;
                if (rightWidth < PAGE_DIGIT_WIDTH || rightWidth % PAGE_DIGIT_WIDTH !== 0)
                    continue;
                const rightCount = rightWidth / PAGE_DIGIT_WIDTH;
                const right = decodeDigitSlots(line, atlas, rightStartX, rightCount, slashGlyph);
                if (!right)
                    continue;
                const text = `[${left}/${right}]`;
                if (!PAGE_INDICATOR_PATTERN.test(text))
                    continue;
                if (!composedTextMatches(line, openX, atlas, text))
                    continue;
                results.push(text);
            }
        }
    }
    return results;
}
/** Locate `[`, `/`, and `]` by exact anchors and logical binary masks. Their
 * positions define fixed 7px digit slots, which are decoded by exact anchor and
 * composed binary-mask equality. The final complete sequence is confirmed once
 * more with its full overhang-aware composed mask.
 */
export function decodeOwlPageIndicatorSlots(image, options = {}) {
    const line = pageLine(image);
    if (!line)
        return '';
    return uniqueResult(atlasChoices(options).flatMap((atlas) => decodeSlotsWithAtlas(line, atlas)));
}
/** Production page-indicator decoder. The deterministic slot decoder is used
 * first; the delimiter-located left-to-right decoder is retained as a fallback
 * for forward compatibility if page digit geometry changes while glyph grammar
 * remains the same.
 */
export function decodeOwlPageIndicator(image, options = {}) {
    return decodeOwlPageIndicatorSlots(image, options)
        || decodeOwlPageIndicatorLeftToRight(image, options);
}
//# sourceMappingURL=page-indicator.js.map